import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import Icons from '@aurora/shared-client/icons';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type {
  AcceptedSolutionMessage,
  Message,
  MessageSearchEdge,
  QuickMessageSearchResult,
  TopLevelMessageSearchResult
} from '@aurora/shared-generated/types/graphql-schema-types';
import { SearchSnippetKey } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import React, { useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type {
  MessageSearchViewFragment,
  QuickMessageSearchViewFragment
} from '../../../../types/graphql-types';
import useTranslation from '../../../useTranslation';
import MessageLink from '../../MessageLink/MessageLink';
import MessageViewInline from '../../MessageView/MessageViewInline/MessageViewInline';
import type {
  MessageViewCommonProps,
  MessageViewInlineProps,
  SearchData
} from '../../MessageView/types';
import localStyes from './MessageSearchViewInline.module.pcss';
import type {
  MessagePageOrReplyPageAndParams,
  MessageReplyPageParams
} from '@aurora/shared-client/routes/endUserRoutes';
import { getMessageRouteAndParams } from '../../../../helpers/messages/MessageHelper/MessageHelper';
import UrlHelper from '@aurora/shared-utils/helpers/urls/UrlHelper/UrlHelper';
import useSeoProperties from '@aurora/shared-client/components/seo/useSeoProperties';

interface MessageSearchProps {
  /**
   * The entity to view
   */
  entity: MessageSearchViewFragment | QuickMessageSearchViewFragment;
  /**
   * Classname to apply to the view
   */
  className?: string;
  /**
   * Search specific data for a message
   */
  searchData: SearchData;
  /**
   * Inline view message props
   */
  messageInlineViewProps: MessageViewCommonProps | MessageViewInlineProps;
  /**
   *  Display matching replies
   */
  useMatchingReplies?: boolean;
}

const MessageSearchViewInline: React.FC<React.PropsWithChildren<MessageSearchProps>> = ({
  entity,
  className,
  searchData,
  messageInlineViewProps,
  useMatchingReplies
}) => {
  const message =
    entity.__typename === 'TopLevelMessageSearchResult'
      ? (entity as TopLevelMessageSearchResult).message
      : (entity as QuickMessageSearchResult).message;
  const descendants =
    entity.__typename === 'TopLevelMessageSearchResult'
      ? (entity as TopLevelMessageSearchResult).descendants
      : null;
  const cx = useClassNameMapper(localStyes);
  const [showTopLevelReplies, setShowTopLevelReplies] = useState<boolean>(false);
  const { formatMessage } = useTranslation(EndUserComponent.MESSAGE_SEARCH_VIEW_INLINE);
  const { router, Link } = useEndUserRoutes();
  const { getCaseSensitivePath } = useSeoProperties();
  const matchingRepliesToDisplay = 3;

  /**
   * This function returns the list of matching replies for a search term
   */
  function renderMatchingRepliesList() {
    const matchingReplies = descendants.edges.slice(0, matchingRepliesToDisplay);
    return matchingReplies.map((reply: MessageSearchEdge) => {
      let messageBodySnippet: string;
      if (reply.node.snippet?.length > 0) {
        messageBodySnippet = reply.node.snippet.find(
          snippet => snippet.key === SearchSnippetKey.Body
        )?.content[0];
      } else {
        messageBodySnippet = reply.node.message.body;
      }

      const solution = (reply?.node?.message as AcceptedSolutionMessage)?.solution;
      const { route, params } = getMessageRouteAndParams(reply.node.message);
      const finalParams = {
        ...params,
        messageSubject: getCaseSensitivePath(UrlHelper.determineSlugForMessagePath(message)),
        boardId: getCaseSensitivePath(message.board.displayId),
        replyId: reply?.node?.message.uid.toString()
      };
      return (
        <Link<MessagePageOrReplyPageAndParams>
          route={route}
          params={finalParams as unknown as MessageReplyPageParams}
          passHref
          legacyBehavior={true}
          key={reply.node.message.id}
        >
          <a
            className={cx('lia-message-reply')}
            aria-label={reply.node.message.subject}
            data-testid="MessageLink"
          >
            <Icon
              className={cx('flex-shrink-0')}
              icon={solution ? Icons.CheckmarkIcon : Icons.CommentIcon}
              color={solution ? IconColor.GRAY_600 : IconColor.SOLVED}
              size={IconSize.PX_16}
            />
            <span dangerouslySetInnerHTML={{ __html: messageBodySnippet }} />
          </a>
        </Link>
      );
    });
  }

  /**
   * This function returns a section of matching replies and an option to view all replies for a message
   */
  function renderMatchingRepliesSection() {
    return (
      <div data-testid="MessageSearch.RepliesSection" className={cx('lia-wrap')}>
        {renderMatchingRepliesList()}
        {descendants.edges.length > matchingRepliesToDisplay && (
          <MessageLink message={message} className={cx('lia-show-all')}>
            <Icon icon={Icons.ChevronDownIcon} size={IconSize.PX_12} color={IconColor.LOAD_TEXT} />
            {formatMessage('messageShowAllReplies')}
          </MessageLink>
        )}
      </div>
    );
  }

  /**
   * Renders a link to indicate that matching replies are available for a message
   */
  function renderShowRepliesLink() {
    return (
      <Button
        title={formatMessage('messageRepliesShowMoreLink')}
        aria-label={formatMessage('messageRepliesShowMoreLinkAriaLabel', {
          subject: message.subject
        })}
        variant={ButtonVariant.LINK}
        onClick={() => setShowTopLevelReplies(true)}
        className={cx('lia-g-link-btn')}
        data-testid="MessageSearch.RepliesPager"
      >
        <Icon
          icon={Icons.ChevronDownIcon}
          className={cx('lia-g-mr-5')}
          color={IconColor.LOAD_TEXT}
          size={IconSize.PX_16}
        />
        {formatMessage('messageRepliesShowMoreLink')}
      </Button>
    );
  }

  const hasReplies = descendants?.edges.length > 0;
  const searchOnlyInTopics =
    router.getUnwrappedQueryParam(EndUserQueryParams.SEARCH_ONLY_IN_TOPICS) === 'true';

  const useMatchingRepliesSection = useMatchingReplies && showTopLevelReplies;
  const useShowRepliesLink =
    useMatchingReplies && !showTopLevelReplies && hasReplies && !searchOnlyInTopics;

  return (
    <>
      <MessageViewInline
        entity={message as Message}
        searchData={searchData}
        className={className}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...messageInlineViewProps}
        subFooter={
          (useMatchingRepliesSection || useShowRepliesLink) && (
            <>
              {useMatchingRepliesSection && renderMatchingRepliesSection()}
              {useShowRepliesLink && renderShowRepliesLink()}
            </>
          )
        }
      />
    </>
  );
};

export default MessageSearchViewInline;
